import {
  ChangeDetectionStrategy,
  Component,
  HostBinding,
  Input,
} from '@angular/core'

/**
 * Defines the available variants for typography.
 */
export type TypographyVariant =
  | 'h1'
  | 'h2'
  | 'h3'
  | 'h4'
  | 'h5'
  | 'body'
  | 'emphasized'
  | 'monospace'

/**
 * Defines the size options for scalable typography variants (body, emphasized, monospace).
 */
export type TypographySize = 'sm' | 'md' | 'lg'

/**
 * Defines the display options for typography.
 */
export type DisplayType = 'block' | 'inline' | 'inline-block'

/**
 * Defines the available color options for typography styling.
 */
export type TypographyColor =
  | 'primary'
  | 'secondary'
  | 'tertiary'
  | 'light'
  | 'dark'
  | 'inactive'
  | 'destructive'
  | 'highlight'
  | 'success'
  | 'error'
  | 'info'
  | 'warning'
  | 'body'
  | 'inherit'

/**
 * Defines the text alignment options for typography.
 */
export type Alignment = 'left' | 'center' | 'right' | 'justify' | 'inherit'

@Component({
  selector: 'hyperkit-typography',
  standalone: true,
  templateUrl: './typography.component.html',
  styleUrls: ['./typography.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TypographyComponent {
  /**
   * The variant for the typography styling.
   * Headers (h1-h5) have fixed sizes, while body/emphasized/monospace can be sized.
   */
  @Input() variant: TypographyVariant = 'body'

  /**
   * The size for scalable variants (body, emphasized, monospace).
   * Only applies when variant is 'body', 'emphasized', or 'monospace'.
   * Defaults to 'md' for consistency.
   */
  @Input() size: TypographySize = 'md'

  /**
   * The color variant for the typography styling.
   */
  @Input() color: TypographyColor = 'primary'

  /**
   * The text alignment for the typography.
   */
  @Input() align: Alignment = 'left'

  /**
   * Whether the text should wrap.
   * If false, text will not wrap, and overflow will be handled with ellipsis.
   */
  @Input() wrap = true

  /**
   * The display type for the typography element.
   * Controls how the element is rendered in the document flow.
   */
  @Input() display: DisplayType = 'block'

  /**
   * Controls how long words (or unbroken strings) break within the container.
   * Default is 'normal'. Set to 'break-word' or 'break-all' for force-wrapping.
   */
  @Input() wordBreak: 'normal' | 'break-word' | 'break-all' = 'normal'

  /**
   * Additional CSS classes to apply to the typography element.
   */
  @Input() additionalClasses: string[] = []

  /**
   * Dynamic attributes for the element, such as `aria-label` or `data-*` attributes.
   */
  @Input() attributes: Record<string, string | number | boolean> = {}

  /**
   * Checks if the current variant supports sizing.
   */
  private get isScalableVariant(): boolean {
    return ['body', 'emphasized', 'monospace'].includes(this.variant)
  }

  /**
   * Computes the full list of classes to apply to the typography element.
   * Combines the variant, size (if applicable), color, alignment, wrap, and additional classes.
   *
   * @returns A string of space-separated class names.
   */
  get classes(): string {
    const variantClass = this.variant && `variant-${this.variant}`

    // Only add size class for scalable variants
    const sizeClass = this.isScalableVariant ? `size-${this.size}` : null

    const wrapClass = this.wrap ? 'wrap' : 'no-wrap'
    const colorClass = this.color && `color-${this.color}`
    const alignmentClass = this.align && `align-${this.align}`
    const displayClass = this.display && `display-${this.display}`
    const wordBreakClass =
      this.wordBreak === 'break-word'
        ? 'word-break-word'
        : this.wordBreak === 'break-all'
          ? 'word-break-all'
          : null

    return [
      variantClass,
      sizeClass,
      colorClass,
      alignmentClass,
      wrapClass,
      displayClass,
      wordBreakClass,
      ...this.additionalClasses,
    ]
      .filter(Boolean)
      .join(' ')
  }

  @HostBinding('class') get hostClasses(): string {
    const classes = [`display-${this.display}`]
    return classes.filter(Boolean).join(' ')
  }
}
